<!--
  THE CELEBRATION (spec 53 section 8.2). Celebrate a pass, never an attempt.

  Four states:
    module      Module complete: the title, what it unlocks, "You did 9 of 11
                steps on your own" when anything was done for them, then
                Next and Back to training. A short confetti burst.
    course      The same card with the course's own line, and a longer burst.
    account     The last required module passed and the receipt said
                `cleared: true`: "Your account is open." The modules they
                passed and Go to my account. A burst.
    not-passed  No confetti. "Almost there." Which steps were done for them,
                which were skipped and which are still to do (each card's
                title once), about how long trying them takes, Try again
                and Watch the video again. No blame (spec 33's copy rules).

  Under reduced motion a still check-mark badge replaces the burst, and the
  confetti chunk is never fetched at all. `canvas-confetti` (1.9.3, ISC) is a
  lazy chunk loaded at the moment of a pass, so nobody who never passes a
  module downloads it; `disableForReducedMotion` is set too, for a preference
  that changes while the page is open.

  Announced in a polite live region; focus goes to the primary button. This
  supersedes spec 25 6.2's "No confetti" for training only.
-->
<template>
  <div class="lw-c-celebration" data-test="celebration" @keydown.esc="$emit('back')">
    <section
      class="lw-c-celebration__card"
      :class="`lw-c-celebration__card--${state}`"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="headingId"
    >
      <div
        v-if="showBadge"
        class="lw-c-celebration__badge"
        data-test="celebration-badge"
        aria-hidden="true"
      >
        <i class="fa fa-check" />
      </div>

      <p v-if="eyebrow" class="lw-c-celebration__eyebrow">{{ eyebrow }}</p>
      <h2 :id="headingId" class="lw-c-celebration__title" data-test="celebration-title">
        {{ heading }}
      </h2>

      <template v-if="state === 'module' || state === 'course'">
        <p v-if="state === 'course' && courseLine" class="lw-c-celebration__line">
          {{ courseLine }}
        </p>
        <p v-if="unlocks" class="lw-c-celebration__line">{{ unlocks }}</p>
        <p v-if="ownStepsLine" class="lw-c-celebration__note" data-test="celebration-own-steps">
          {{ ownStepsLine }}
        </p>
      </template>

      <template v-else-if="state === 'account'">
        <p v-if="passedTitles.length" class="lw-c-celebration__line">You passed:</p>
        <ul v-if="passedTitles.length" class="lw-c-celebration__list">
          <li v-for="name in passedTitles" :key="name">{{ name }}</li>
        </ul>
      </template>

      <template v-else-if="state === 'not-passed'">
        <p class="lw-c-celebration__line" data-test="celebration-missing">{{ missingLine }}</p>
      </template>

      <div class="lw-c-celebration__actions">
        <template v-if="state === 'account'">
          <LwButton ref="primary" variant="primary" :href="accountUrl">Go to my account</LwButton>
          <p class="lw-c-celebration__note">
            The rest of training stays here whenever you want it.
          </p>
          <LwButton variant="secondary" @click="$emit('back')">Back to training</LwButton>
        </template>
        <template v-else-if="state === 'not-passed'">
          <LwButton ref="primary" variant="primary" @click="$emit('retry')">Try again</LwButton>
          <LwButton variant="secondary" @click="$emit('watch')">Watch the video again</LwButton>
        </template>
        <template v-else-if="nextTitle">
          <LwButton ref="primary" variant="primary" @click="$emit('next')">
            Next: {{ nextTitle }}
          </LwButton>
          <LwButton variant="secondary" @click="$emit('back')">Back to training</LwButton>
        </template>
        <template v-else>
          <LwButton ref="primary" variant="primary" @click="$emit('back')">
            Back to training
          </LwButton>
        </template>
      </div>
    </section>
    <p class="lw-c-celebration__live" aria-live="polite" data-test="celebration-live">
      {{ announcement }}
    </p>
  </div>
</template>

<script>
import LwButton from './LwButton.vue';

const STATES = ['module', 'course', 'account', 'not-passed'];
const DEFAULT_ORIGIN = Object.freeze({ x: 0.5, y: 0.55 });
// The not-passed card's sentences, in this order, by what happened to a step
// (grade.js `reason`). Anything else (not done, or evidence the practice
// tenant doesn't show) is still to do.
const MISSING_GROUPS = Object.freeze([
  {
    matches: reason => reason === 'shown',
    one: 'This step was done for you',
    many: 'These steps were done for you',
  },
  {
    matches: reason => reason === 'skipped',
    one: 'This step was skipped',
    many: 'These steps were skipped',
  },
  {
    matches: reason => reason !== 'shown' && reason !== 'skipped',
    one: 'Still to do',
    many: 'Still to do',
  },
]);

let headingSeq = 0;

function prefersReducedMotion() {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/**
 * The confetti library, fetched as its own chunk at the moment it is wanted.
 * The production build's `confetti` cache group (build/webpack.prod.conf.js)
 * is what keeps it out of the eager vendor bundle; a webpackChunkName comment
 * here would collide with that group's name and fold it back in.
 */
function loadConfetti() {
  return import('canvas-confetti').then(mod => mod.default || mod);
}

export default {
  name: 'LwCelebration',
  components: { LwButton },
  props: {
    state: {
      type: String,
      required: true,
      validator: value => STATES.includes(value),
    },
    // The module's title ("Assign it").
    title: { type: String, default: '' },
    // Its `training.unlocks` line.
    unlocks: { type: String, default: '' },
    // Graded steps, and how many of them the person did on their own. The
    // own-steps line shows only when something was done for them.
    ownSteps: { type: Number, default: 0 },
    totalSteps: { type: Number, default: 0 },
    // The next chapter's title, for "Next: Assign it"; none on the last.
    nextTitle: { type: String, default: '' },
    // A course's closing line ("You've taken a load from a phone call to
    // delivered").
    courseLine: { type: String, default: '' },
    // Not passed: the steps that didn't count, [{ title, reason }].
    missing: { type: Array, default: () => [] },
    // Not passed: about how long trying again takes.
    minutes: { type: Number, default: 0 },
    // Account open: the titles of the modules they passed.
    passedTitles: { type: Array, default: () => [] },
    // Account open: where their account is.
    accountUrl: { type: String, default: 'https://app.ladeway.com/' },
    // Where the burst comes from, as canvas-confetti's 0..1 origin (the card's
    // position at the end of the run).
    origin: { type: Object, default: () => DEFAULT_ORIGIN },
    // The chunk loader. A seam for tests; the default is the lazy import.
    confetti: { type: Function, default: loadConfetti },
  },
  emits: ['next', 'back', 'retry', 'watch'],
  data() {
    headingSeq += 1;
    return {
      headingId: `lw-celebration-title-${headingSeq}`,
      reducedMotion: prefersReducedMotion(),
      announcement: '',
    };
  },
  computed: {
    passed() {
      return this.state !== 'not-passed';
    },
    showBadge() {
      return this.passed && this.reducedMotion;
    },
    eyebrow() {
      if (this.state === 'module') return 'Module complete';
      if (this.state === 'course') return 'Course complete';
      return '';
    },
    heading() {
      if (this.state === 'account') return 'Your account is open.';
      if (this.state === 'not-passed') return 'Almost there.';
      return this.title || 'Module complete';
    },
    ownStepsLine() {
      if (!this.totalSteps || this.ownSteps >= this.totalSteps) return '';
      return `You did ${this.ownSteps} of ${this.totalSteps} steps on your own.`;
    },
    missingLine() {
      // The grader's answer for a module with nothing graded yet (grade.js).
      if (this.missing.some(m => m && m.reason === 'not-ready')) {
        return "Practice for this module isn't ready yet, so it can't be passed today.";
      }
      const about = this.minutes > 0 ? ` it takes about ${this.minutes} minutes` : '';
      // Each step is named under what happened to it (grade.js `reason`):
      // done by "Do this step for me", skipped, or not done. A card's steps
      // share its title, so a title is named once in each sentence.
      const groups = MISSING_GROUPS.map(group => ({
        ...group,
        names: [
          ...new Set(
            this.missing
              .filter(m => m && group.matches(m.reason))
              .map(m => m.title || '')
              .filter(Boolean)
          ),
        ],
      })).filter(group => group.names.length);
      if (!groups.length) {
        return `A few steps were done for you. Try them yourself;${about || ' it only takes a moment'}.`;
      }
      const count = new Set(groups.flatMap(group => group.names)).size;
      const said = groups.map(({ one, many, names }) => {
        const words = names.length === 1 ? one : many;
        return `${words}: ${names.join(', ')}.`;
      });
      const tail = about ? `;${about}` : '';
      return `${said.join(' ')} Try ${count === 1 ? 'it' : 'them'} yourself${tail}.`;
    },
    spoken() {
      if (this.state === 'account') return 'Your account is open.';
      if (this.state === 'not-passed') return `Almost there. ${this.missingLine}`;
      return `${this.eyebrow}: ${this.title}.`;
    },
  },
  mounted() {
    this.$nextTick(() => {
      // Set after mount, so the region already exists when the words arrive
      // (a region created with its text is often not read at all).
      this.announcement = this.spoken;
      const primary = this.$refs.primary;
      const el = primary && (primary.$el || primary);
      if (el && typeof el.focus === 'function') el.focus();
    });
    if (this.passed && !this.reducedMotion) this.burst();
  },
  methods: {
    async burst() {
      let fire;
      try {
        fire = await this.confetti();
      } catch (err) {
        return; // No confetti is a celebration still.
      }
      if (typeof fire !== 'function') return;
      const base = { origin: this.origin, disableForReducedMotion: true, zIndex: 1100 };
      fire({ ...base, particleCount: 70, spread: 70, startVelocity: 35 });
      if (this.state === 'module') return;
      // A course or the account: a longer burst, three waves.
      setTimeout(() => fire({ ...base, particleCount: 60, spread: 100, angle: 60 }), 250);
      setTimeout(() => fire({ ...base, particleCount: 60, spread: 100, angle: 120 }), 500);
    },
  },
};
</script>

<style scoped>
.lw-c-celebration {
  position: fixed;
  inset: 0;
  z-index: var(--lw-z-dialog);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--lw-space-4);
  background: var(--lw-elev-scrim-surface);
}

.lw-c-celebration__card {
  width: 100%;
  max-width: 28rem;
  display: flex;
  flex-direction: column;
  gap: var(--lw-space-3);
  padding: var(--lw-space-6);
  background: var(--lw-elev-4-surface);
  border: 1px solid var(--lw-elev-4-line);
  border-radius: var(--lw-radius-lg);
  box-shadow: var(--lw-elev-4-shadow);
  color: var(--lw-fg-primary);
  text-align: center;
}

.lw-c-celebration__badge {
  align-self: center;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3.5rem;
  height: 3.5rem;
  border-radius: var(--lw-radius-pill);
  background: var(--lw-success-bg);
  border: 2px solid var(--lw-success-edge);
  color: var(--lw-success-fg);
  font-size: 1.75rem;
}

.lw-c-celebration__eyebrow {
  margin: 0;
  font: var(--lw-type-label);
  letter-spacing: var(--lw-type-label-tracking);
  text-transform: var(--lw-type-label-transform);
  color: var(--lw-success-fg);
}

.lw-c-celebration__title {
  margin: 0;
  font: var(--lw-type-title);
}

.lw-c-celebration__line {
  margin: 0;
  font: var(--lw-type-body);
}

.lw-c-celebration__note {
  margin: 0;
  font: var(--lw-type-body-sm);
  color: var(--lw-fg-secondary);
}

.lw-c-celebration__list {
  margin: 0;
  padding: 0;
  list-style: none;
  font: var(--lw-type-body-sm);
  color: var(--lw-fg-secondary);
}

.lw-c-celebration__actions {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: var(--lw-space-3);
  margin-top: var(--lw-space-2);
}

.lw-c-celebration__live {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  white-space: nowrap;
}
</style>
